import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Box, Button, Stack, Text } from "@mantine/core";
import { WhatWeOfferIconTiles } from "@/components/shared/CustomerOfferWhatWeOffer";
import {
  CUSTOMER_OFFER_OTHER_LABEL,
  serializeCustomerOfferHighlightsForApi,
} from "@/shared/constants/customerOfferPresets";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";

export const CUSTOMER_OFFER_BANNER_FORM_ID = "customer-offer-highlight-banner-form";

const SAVE_BUTTON_STYLES = {
  backgroundColor: "#04305c",
  color: "#ffc300",
} as const;

export type CustomerOfferBannerSaveState = {
  canSave: boolean;
  saving: boolean;
};

type CustomerOfferHighlightBannerFormProps = {
  companyId: string;
  onSaved?: () => void | Promise<void>;
  /** When false, use an external Save button linked via `form` attribute. */
  showSaveButton?: boolean;
  onSaveStateChange?: (state: CustomerOfferBannerSaveState) => void;
};

export function CustomerOfferHighlightSaveButton({
  canSave,
  saving,
}: CustomerOfferBannerSaveState) {
  return (
    <Button
      type="submit"
      form={CUSTOMER_OFFER_BANNER_FORM_ID}
      size="sm"
      disabled={!canSave}
      loading={saving}
      style={SAVE_BUTTON_STYLES}
    >
      Save
    </Button>
  );
}

export default function CustomerOfferHighlightBannerForm({
  companyId,
  onSaved,
  showSaveButton = true,
  onSaveStateChange,
}: CustomerOfferHighlightBannerFormProps) {
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");
  const [saving, setSaving] = useState(false);

  const serializedOffers = useMemo(
    () => serializeCustomerOfferHighlightsForApi(selectedPresets, otherText),
    [selectedPresets, otherText],
  );

  const hasOtherSelected = selectedPresets.includes(CUSTOMER_OFFER_OTHER_LABEL);
  const otherTextMissing = hasOtherSelected && !otherText.trim();
  const canSave = serializedOffers.length > 0 && !otherTextMissing;

  useEffect(() => {
    onSaveStateChange?.({ canSave, saving });
  }, [canSave, saving, onSaveStateChange]);

  const togglePreset = useCallback((label: string) => {
    setSelectedPresets((prev) => {
      if (prev.includes(label)) {
        const next = prev.filter((p) => p !== label);
        if (label === CUSTOMER_OFFER_OTHER_LABEL) {
          setOtherText("");
        }
        return next;
      }
      return [...prev, label];
    });
  }, []);

  const handleSave = async () => {
    if (!canSave || saving) return;

    setSaving(true);
    try {
      await trpc.company.update.mutate({
        companyId,
        data: {
          customerOfferHighlight: serializedOffers,
        },
        profileSaveKind: "edit",
      });
      await onSaved?.();
      successNotification({
        title: "Saved",
        message: "Your offers are now on your profile.",
      });
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSave();
  };

  return (
    <Box
      component="form"
      id={CUSTOMER_OFFER_BANNER_FORM_ID}
      onSubmit={handleSubmit}
      mt="xs"
    >
      <Stack gap="sm">
        <WhatWeOfferIconTiles
          selectedLabels={selectedPresets}
          onToggleLabel={togglePreset}
          size="compact"
          layout="responsive"
          justify="flex-start"
          dense
          boldLabels
          otherText={otherText}
          onOtherTextChange={hasOtherSelected ? setOtherText : undefined}
        />

        {otherTextMissing ? (
          <Text size="xs" c="red.7">
            Please describe your offer (max 30 characters) before saving.
          </Text>
        ) : null}

        {showSaveButton ? (
          <CustomerOfferHighlightSaveButton canSave={canSave} saving={saving} />
        ) : null}
      </Stack>
    </Box>
  );
}
